import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Terms of Service — governs use of FleetCost.App.
 * Aligned with South Africa's Consumer Protection Act (CPA) and Electronic
 * Communications and Transactions Act (ECTA). Kept readable — legalese only
 * where it actually matters.
 */
const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6"
          data-testid="terms-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>

        <header className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-slate-900" data-testid="terms-heading">
            Terms of Service
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Effective date: 28&nbsp;September&nbsp;2026 &middot; Operator: <strong>Fleet Cost</strong>
          </p>
        </header>

        <article className="prose prose-slate max-w-none space-y-6 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900">1. Who we are</h2>
            <p>
              &ldquo;<strong>FleetCost.App</strong>&rdquo; (the <strong>Service</strong>) is operated by
              <strong> Fleet Cost</strong> (<strong>we</strong>, <strong>us</strong>, <strong>our</strong>),
              located at 52 Buffel Str, Hoedspruit, 1380, South Africa. You can reach us at{' '}
              <a className="text-indigo-600 hover:underline" href="mailto:hpbza@icloud.com">
                hpbza@icloud.com
              </a>
              . By creating an account or using the Service, you (<strong>you</strong>, the{' '}
              <strong>customer</strong>) agree to these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">2. Your account</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>You must be at least 18 and legally able to enter contracts in South Africa.</li>
              <li>You are responsible for keeping your password confidential and for everything that happens on your account.</li>
              <li>You must give us accurate information at sign-up and keep it up to date.</li>
              <li>You may invite team members. You remain responsible for what your team members do inside your tenant.</li>
              <li>We may suspend or close an account that abuses the Service, breaches these Terms, or is used unlawfully.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">3. What the Service does</h2>
            <p>
              FleetCost.App helps you track vehicle costs, fuel efficiency, services, licences, incidents and
              related fleet data. It is an <strong>administrative tool</strong>. It is not accounting, tax,
              insurance or legal advice, and it does not replace statutory record-keeping obligations. You are
              responsible for the accuracy of the data you enter and the decisions you take based on it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">4. Acceptable use</h2>
            <p>You agree <strong>not</strong> to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the Service to store or transmit unlawful, defamatory, infringing or malicious content.</li>
              <li>Reverse-engineer, decompile, scrape at scale, or attempt to derive our source code.</li>
              <li>Interfere with the Service&rsquo;s security, availability or integrity.</li>
              <li>Resell, sublicence or share access to your account outside your own organisation without our written permission.</li>
              <li>Upload personal information about individuals who have not consented, in breach of POPIA or applicable law.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">5. Fees, trial and payment</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Paid plans are billed in advance in South African Rand (ZAR) unless we tell you otherwise.</li>
              <li>Free trials, if offered, convert to a paid plan at the end of the trial unless you cancel first.</li>
              <li>Fees are non-refundable except where required by the Consumer Protection Act.</li>
              <li>Prices may change with 30 days&rsquo; notice; changes only take effect on your next renewal.</li>
              <li>If payment fails, we may suspend access after a reasonable notice period until payment is made good.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">6. Your data</h2>
            <p>
              You retain all rights to the fleet, cost, supplier, driver and vehicle data you upload
              (<strong>Customer Data</strong>). You grant us a limited licence to process Customer Data solely to
              operate and improve the Service for you. We handle personal information under our{' '}
              <Link className="text-indigo-600 hover:underline" to="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">7. Availability &amp; support</h2>
            <p>
              We aim for high availability but the Service is provided on an <em>as-is</em> and{' '}
              <em>as-available</em> basis. We do not guarantee uninterrupted access. We provide reasonable
              support via <a className="text-indigo-600 hover:underline" href="mailto:hpbza@icloud.com">hpbza@icloud.com</a>{' '}
              during South African business hours.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">8. Liability</h2>
            <p>
              To the maximum extent allowed by South African law (including the Consumer Protection Act and
              ECTA), our aggregate liability to you for any claim arising out of the Service is capped at the
              fees you paid us in the 3 months before the event giving rise to the claim. We are not liable
              for indirect, consequential, incidental or special damages, or for loss of profit, data, goodwill
              or revenue. Nothing in this clause limits liability that cannot lawfully be limited (e.g. for
              wilful misconduct or gross negligence).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">9. Termination</h2>
            <p>
              You may close your account at any time from Profile &rarr; Delete Account or by emailing us. On
              termination we retain your Customer Data for 30 days so you can export it, after which it is
              deleted or anonymised except where we are legally required to retain it. We may terminate for
              material breach on written notice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">10. Changes to these Terms</h2>
            <p>
              We may update these Terms. We&rsquo;ll email account owners at least 14 days before material
              changes take effect. Continued use after the effective date means you accept the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">11. Governing law &amp; disputes</h2>
            <p>
              These Terms are governed by the laws of the Republic of South Africa. The parties submit to the
              non-exclusive jurisdiction of the South African courts. We&rsquo;ll first try to resolve any
              dispute in good faith by email or phone before either party litigates.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">12. Contact</h2>
            <p>
              Fleet Cost &middot; 52 Buffel Str, Hoedspruit, 1380, South Africa &middot;{' '}
              <a className="text-indigo-600 hover:underline" href="mailto:hpbza@icloud.com">hpbza@icloud.com</a>
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default TermsPage;
